import { Box, Card, Chip, Stack } from '@mui/material';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { Typography } from '~/components/Typography';
import { SeverityChip } from './SeverityChip';
import type { Announcement } from './announcements.generated';

export interface AnnouncementCardProps {
  announcement: Announcement;
  href?: string;
  clip?: boolean;
  onOpen?: () => void;
}

export const AnnouncementCard = ({
  announcement,
  href,
  clip = false,
  onOpen,
}: AnnouncementCardProps) => {
  const { t } = useTranslation();

  const link = href
    ? {
        component: 'a' as const,
        href,
        target: '_blank',
        rel: 'noopener',
        onClick: onOpen,
      }
    : {};

  return (
    <Card {...link} sx={{ display: 'block', mb: 2, textDecoration: 'none' }}>
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'borders.main',
          display: 'flex',
          gap: 1,
          p: '12px',
        }}
      >
        <Typography sx={{ flex: 1, fontSize: '14px', fontWeight: 600, minWidth: 0 }}>
          {announcement.title}
        </Typography>
        <SeverityChip severity={announcement.severity} />
      </Box>

      <Box sx={{ p: 2 }}>
        <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
            {DateTime.fromISO(announcement.publishedAt).toLocaleString(DateTime.DATE_MED)}
          </Typography>
          {!announcement.read && (
            <Chip label={t('components.announcements.unread')} size="small" color="success" />
          )}
          {announcement.tags.map((tag) => (
            <Chip key={tag} label={tag} size="small" />
          ))}
        </Stack>

        <Box
          sx={(theme) =>
            clip
              ? {
                  maxHeight: 220,
                  overflow: 'hidden',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 56,
                    background: `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`,
                  },
                }
              : {}
          }
        >
          <Box
            dangerouslySetInnerHTML={{ __html: announcement.body }}
            sx={{
              color: 'text.primary',
              fontSize: '0.875rem',
              lineHeight: 1.7,
              whiteSpace: 'normal',
              '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1, my: 2 },
              '& h1': { fontSize: '1rem', fontWeight: 600, mt: 3, mb: 1 },
              '& ul, & ol': { pl: 3 },
              '& li': { mb: 0.5 },
              '& :first-of-type': { mt: 0 },
              '& :last-child': { mb: 0 },
            }}
          />
        </Box>
      </Box>
    </Card>
  );
};
